import { SelectBadge, TextArea, TextInputProps } from '@cint/wonderment';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

interface TextAreaFieldProps
  extends Omit<TextInputProps, 'value' | 'onChange'> {
  name: string;
  label?: string;
  description?: string;
}

export const TextAreaMultiSelectField = ({
  name,
  label,
  description,
  ...props
}: TextAreaFieldProps) => {
  const [inputValue, setInputValue] = useState('');
  const { control } = useFormContext();

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const raw = e.target.value;

    const parts = raw.split(/[,\s]+/);

    const current = parts.pop() ?? '';

    const newBadges = parts.map((p) => p.trim()).filter((p) => p.length > 0);

    onChange([...(value || []), ...newBadges]);
    setInputValue(current);
  };

  const handleBlur = () => {
    if (inputValue.trim() !== '') {
      onChange([...(value || []), inputValue.trim()]);
      setInputValue('');
    }
  };

  const handleRemoveClick = (badgeText: string) => {
    const newValues = (value || []).filter((val: string) => val !== badgeText);
    onChange(newValues);
  };

  return (
    <TextArea
      data-testid={props['data-testid']}
      id={name}
      value={inputValue}
      onChange={handleChange}
      decoratorTextProps={{
        ...props.decoratorTextProps,
        description: error?.message || description,
      }}
      hasError={!!error}
      onBlur={handleBlur}
      multiSelectProps={{
        selectedBadges: value?.map((val: string, index: number) => (
          <SelectBadge
            key={`${val}-${index}`}
            onClick={() => handleRemoveClick(val)}
            text={val}
          />
        )),
      }}
      labelProps={{
        label,
        size: 'sm',
        isRequired: true,
      }}
    />
  );
};
